/**
 * API client for ClawFarm backend.
 *
 * BUG-005 GREEN Phase: Updated error handling to use error code mapping.
 * BUG-001 GREEN Phase: Added 401 response handler to clear tokens and redirect to login.
 * Spec 2026-06-30-frontend-token-refresh: 401 → refresh-and-retry interceptor
 *   with concurrent-401 deduplication via module-level refreshPromise.
 */

import type {
  Agent,
  User,
  AgentListResponse,
} from '@/types';
import type { AgentStatus } from '@/types/agent';
import type { ApiError } from './errors';
import { getDisplayMessage } from './errors';
import { buildQueryString } from './api/utils';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';

// Flag to prevent infinite redirect loops
let isRedirecting = false;

// ---------------------------------------------------------------------------
// Refresh-token deduplication (spec: frontend-token-refresh)
//
// When multiple in-flight requests receive 401 simultaneously, they all need
// the same fresh access token. Without coordination we'd fire N refresh calls
// in parallel, which (a) wastes round-trips, (b) races on localStorage writes,
// and (c) causes backend token-rotation conflicts (the refresh token is
// rotated on each call, so the second refresh invalidates the first's result).
//
// The first 401 to win the race sets `refreshPromise`. Subsequent 401s await
// it. When it resolves, they read the new tokens from localStorage and retry.
// On failure, the promise is cleared so the next 401 can try again.
// ---------------------------------------------------------------------------
let refreshPromise: Promise<string> | null = null;

const REFRESH_RETRY_DELAY_MS = 2000;

// Re-export types for convenience
export * from '@/types';

export type { User };

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  public async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;

    // Get auth token and add to headers if available
    const token = getToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...options?.headers as Record<string, string>,
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    let response = await fetch(url, {
      ...options,
      headers,
    });

    // Spec 2026-06-30-frontend-token-refresh: 401 → refresh-and-retry
    //
    // We only attempt a refresh when:
    //   1. The request has NOT already been retried (X-Retry-Auth header absent).
    //   2. The endpoint is not the refresh endpoint itself (would deadlock).
    //   3. The user is not already on /login (no auth needed for login page).
    //   4. A refresh_token is stored in localStorage.
    const isRetried = (options?.headers as Record<string, string> | undefined)?.[
      'X-Retry-Auth'
    ] === '1';
    const isRefreshEndpoint = endpoint.includes('/auth/refresh');
    const onLoginPage = window.location.pathname.includes('/login');
    const hasRefreshToken = !!getRefreshToken();

    if (
      response.status === 401 &&
      !isRetried &&
      !isRefreshEndpoint &&
      !onLoginPage &&
      hasRefreshToken
    ) {
      try {
        await this.refreshAccessToken();
      } catch {
        // Refresh failed (expired/revoked refresh token, or repeated 5xx).
        // Fall through to the standard clear-and-redirect path.
        removeToken();
        removeRefreshToken();
        removeUser();
        if (!isRedirecting) {
          isRedirecting = true;
          window.location.href = '/login?session=expired';
        }
        // Parse the 401 body for the caller as if it were the original failure.
        const errorData = await this.safeParseError(response);
        throw new ApiErrorClass(
          errorData.error || { code: 'SESSION_EXPIRED', message: 'Session expired' },
          response.status,
        );
      }

      // Refresh succeeded — retry the original request with the new access token.
      // Build a fresh header map (don't spread `headers`, which still holds
      // the OLD access token from the start of this request) so the retry
      // uses the rotated token written by refreshAccessToken().
      const newToken = getToken();
      const retryHeaders: Record<string, string> = {
        'Content-Type': 'application/json',
        ...options?.headers as Record<string, string>,
        'X-Retry-Auth': '1',
      };
      if (newToken) {
        retryHeaders['Authorization'] = `Bearer ${newToken}`;
      }

      response = await fetch(url, {
        ...options,
        headers: retryHeaders,
      });
    }

    // BUG-001 GREEN Phase: Handle 401 Unauthorized responses
    if (response.status === 401) {
      // Always clear authentication data on 401
      removeToken();
      removeRefreshToken();
      removeUser();

      // Prevent infinite redirect loops - only redirect if not on login page
      if (!isRedirecting && !window.location.pathname.includes('/login')) {
        isRedirecting = true;
        // Redirect to login page
        window.location.href = '/login?session=expired';
      }

      // Reset flag if we're already on login page
      if (window.location.pathname.includes('/login')) {
        isRedirecting = false;
      }
    }

    if (!response.ok) {
      // Parse error response with error code (BUG-005 GREEN Phase)
      let errorData: any;
      try {
        errorData = await response.json();
      } catch {
        // If response body is not JSON, create a generic error
        errorData = {
          error: {
            code: 'HTTP_ERROR',
            message: `API error: ${response.status}`,
          }
        };
      }

      // Create ApiError object. The backend has two error shapes:
      //   1. FastAPI standard: `{detail: <str|dict>}` (HTTPException)
      //   2. Error-envelope middleware: `{success: false, error: {code, message: <str|dict>}}`
      // When the payload is a structured dict (e.g. agent_limit_reached),
      // JSON-stringify it so downstream ApiErrorClass.message stays a
      // string AND so helpers like parseAgentLimitError can JSON.parse
      // it back into shape. Sending the raw dict would coerce to
      // "[object Object]" via Error's toString, losing the payload.
      const coerce = (m: unknown): string =>
        typeof m === 'string' ? m : JSON.stringify(m);
      const apiError: ApiError = errorData.error
        ? {
            code: errorData.error.code,
            message: coerce(errorData.error.message),
            ...(errorData.error.details ? { details: errorData.error.details } : {}),
          }
        : {
            code: 'HTTP_ERROR',
            message: coerce(errorData.detail ?? errorData.message ?? 'An error occurred'),
          };

      // Throw ApiError for components to handle
      throw new ApiErrorClass(apiError, response.status);
    }

    // Reset redirect flag on successful response
    isRedirecting = false;

    // Handle 204 No Content (e.g. DELETE responses)
    if (response.status === 204) {
      return undefined as T;
    }

    const data = await response.json();
    // Unwrap the response if it has the standard wrapper format
    if (data && typeof data === 'object' && 'data' in data && 'success' in data) {
      return data.data as T;
    }
    return data;
  }

  // Auth endpoints
  async login(email: string, password: string): Promise<{ access_token: string; token_type: string; expires_in: number; user: User }> {
    return this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  async register(data: {
    full_name: string;
    email: string;
    password: string;
    confirm_password: string;
    accept_terms: boolean;
  }): Promise<any> {
    return this.request('/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async logout(): Promise<void> {
    return this.request('/auth/logout', { method: 'POST' });
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<{ message: string }> {
    return this.request('/users/me/password', {
      method: 'PUT',
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
    });
  }

  async getMe(): Promise<User> {
    return this.request('/users/me');
  }

  async refreshToken(refreshToken: string): Promise<any> {
    return this.request('/auth/refresh', {
      method: 'POST',
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
  }

  /**
   * Spec 2026-06-30-frontend-token-refresh: refresh-and-store access token.
   *
   * Uses a module-level `refreshPromise` to deduplicate concurrent 401s:
   * the first 401 in a window sets the promise; subsequent 401s await it.
   *
   * Refresh behaviour:
   *   - 200 OK: store new access_token + refresh_token, resolve with new access_token.
   *   - 401 (expired/revoked refresh): reject — caller falls through to clear+redirect.
   *   - 5xx (server error): retry once after REFRESH_RETRY_DELAY_MS; on second
   *     failure, reject — caller falls through to clear+redirect.
   *
   * The actual fetch bypasses `this.request()` to avoid re-entering the
   * 401 interceptor (which would loop on a 401 from /auth/refresh itself).
   */
  private async refreshAccessToken(): Promise<string> {
    if (refreshPromise) {
      return refreshPromise;
    }

    refreshPromise = (async () => {
      const currentRefresh = getRefreshToken();
      if (!currentRefresh) {
        throw new Error('No refresh token available');
      }

      const doRefresh = async (): Promise<Response> => {
        return fetch(`${this.baseUrl}/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: currentRefresh }),
        });
      };

      let refreshResponse = await doRefresh();

      // 5xx: one retry after a delay (spec US-2 edge case).
      if (refreshResponse.status >= 500) {
        await new Promise((resolve) => setTimeout(resolve, REFRESH_RETRY_DELAY_MS));
        refreshResponse = await doRefresh();
      }

      if (!refreshResponse.ok) {
        throw new Error(`Refresh failed with status ${refreshResponse.status}`);
      }

      const data = await refreshResponse.json();
      // Response is unwrapped from the standard { data, success } wrapper.
      const payload = data && typeof data === 'object' && 'data' in data ? data.data : data;
      const newAccess = payload?.access_token;
      const newRefresh = payload?.refresh_token;

      if (!newAccess) {
        throw new Error('Refresh response missing access_token');
      }

      setToken(newAccess);
      if (newRefresh) {
        setRefreshToken(newRefresh);
      }
      return newAccess;
    })().finally(() => {
      // Clear the cached promise so the next 401 (after a successful refresh
      // window) can attempt a fresh refresh when the rotated access token
      // itself expires later. We clear whether we succeeded or failed.
      refreshPromise = null;
    });

    return refreshPromise;
  }

  /**
   * Best-effort JSON error-body parser used when re-throwing after a 401.
   * Never throws — returns a generic error object on parse failure.
   */
  private async safeParseError(response: Response): Promise<{ error: ApiError }> {
    try {
      return await response.json();
    } catch {
      return {
        error: {
          code: 'HTTP_ERROR',
          message: `API error: ${response.status}`,
        },
      };
    }
  }

  // Agency endpoints
  async getAgency(agencyId: string): Promise<any> {
    return this.request<any>(`/agencies/${agencyId}`);
  }

  async listAgencies(): Promise<{ agencies: any[]; total: number }> {
    return this.request<{ agencies: any[]; total: number }>('/agencies');
  }

  async updateAgency(agencyId: string, data: { name?: string; subscription_plan?: string }): Promise<any> {
    return this.request<any>(`/agencies/${agencyId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Agents - DEPRECATED: Use userAIAgentsApi (from lib/api/user_ai_agents) or admin AI agent endpoints instead.
  // These endpoints no longer exist on the backend. Kept for backward compat imports only.
  /** @deprecated Use userAIAgentsApi from lib/api/user_ai_agents or admin AIAgentService */
  async getAgents(params?: { page?: number; page_size?: number }): Promise<AgentListResponse> {
    const qs = params ? buildQueryString(params) : '';
    return this.request<AgentListResponse>(`/agents${qs}`);
  }

  /** @deprecated Use userAIAgentsApi from lib/api/user_ai_agents or admin AIAgentService */
  async getAgent(id: string): Promise<Agent> {
    return this.request<Agent>(`/agents/${id}`);
  }

  /** @deprecated Use userAIAgentsApi from lib/api/user_ai_agents or admin AIAgentService */
  async getAgentStatus(id: string): Promise<{ status: AgentStatus; last_updated: string }> {
    return this.request<{ status: AgentStatus; last_updated: string }>(`/agents/${id}/status`);
  }

  /** @deprecated Use userAIAgentsApi from lib/api/user_ai_agents or admin AIAgentService */
  async createAgent(data: Partial<Agent>, agencyId?: string): Promise<Agent> {
    const qs = agencyId ? `?agency_id=${agencyId}` : '';
    return this.request<Agent>(`/agents${qs}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /** @deprecated Use userAIAgentsApi from lib/api/user_ai_agents or admin AIAgentService */
  async updateAgent(id: string, data: Partial<Agent>): Promise<Agent> {
    return this.request<Agent>(`/agents/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  /** @deprecated Use userAIAgentsApi from lib/api/user_ai_agents or admin AIAgentService */
  async deleteAgent(id: string): Promise<void> {
    return this.request<void>(`/agents/${id}`, {
      method: 'DELETE',
    });
  }

  // Usage Tracking
  async getUsageSummary(agencyId: string, period: string = 'this_month'): Promise<any> {
    return this.request<any>(`/agencies/${agencyId}/usage/summary?period=${period}`);
  }

  async getAgentUsage(agencyId: string, agentId: string, period: string = 'this_month'): Promise<any> {
    return this.request<any>(`/agencies/${agencyId}/usage/agents/${agentId}?period=${period}`);
  }

  async getTokenUsage(agencyId: string, period: string = 'this_month'): Promise<any> {
    return this.request<any>(`/agencies/${agencyId}/usage/tokens?period=${period}`);
  }
}

const api = new ApiClient();

// Token management helpers
export const getToken = (): string | null => {
  return localStorage.getItem('access_token');
};

export const setToken = (token: string): void => {
  localStorage.setItem('access_token', token);
};

export const removeToken = (): void => {
  localStorage.removeItem('access_token');
};

export const getUser = (): User | null => {
  const userStr = localStorage.getItem('user');
  return userStr ? JSON.parse(userStr) : null;
};

export const setUser = (user: User): void => {
  localStorage.setItem('user', JSON.stringify(user));
};

export const removeUser = (): void => {
  localStorage.removeItem('user');
};

// Refresh-token storage helpers (spec 2026-06-30-frontend-token-refresh).
// Quota / serialisation failures are tolerated — the login/register callers
// wrap their writes; the worst case is "no refresh available" → fall through
// to the legacy 30-minute access-token-only session.
export const getRefreshToken = (): string | null => {
  return localStorage.getItem('refresh_token');
};

export const setRefreshToken = (token: string): void => {
  try {
    localStorage.setItem('refresh_token', token);
  } catch {
    // QuotaExceededError or disabled localStorage — continue without refresh.
  }
};

export const removeRefreshToken = (): void => {
  localStorage.removeItem('refresh_token');
};

// Export API objects for each resource
/** @deprecated Use userAIAgentsApi from lib/api/user_ai_agents or admin AIAgentService */
export const agentsApi = {
  getAll: async (params?: { page?: number; page_size?: number }) => api.getAgents(params),
  getById: async (id: string) => api.getAgent(id),
  getStatus: async (id: string) => api.getAgentStatus(id),
  create: async (data: Partial<Agent>, agencyId?: string) => api.createAgent(data, agencyId),
  update: async (id: string, data: Partial<Agent>) => api.updateAgent(id, data),
  delete: async (id: string) => api.deleteAgent(id),
};

export const usageApi = {
  getSummary: async (agencyId: string, period?: string) => api.getUsageSummary(agencyId, period || 'this_month'),
  getAgentUsage: async (agencyId: string, agentId: string, period?: string) => api.getAgentUsage(agencyId, agentId, period || 'this_month'),
  getTokenUsage: async (agencyId: string, period?: string) => api.getTokenUsage(agencyId, period || 'this_month'),
};

export const authApi = {
  login: async (data: { email: string; password: string }) => api.login(data.email, data.password),
  register: async (data: {
    full_name: string;
    email: string;
    password: string;
    confirm_password: string;
    accept_terms: boolean;
  }) => api.register(data),
  logout: async () => api.logout(),
  refreshToken: async (refreshToken: string) => api.refreshToken(refreshToken),
  changePassword: async (currentPassword: string, newPassword: string) => api.changePassword(currentPassword, newPassword),
  getMe: async () => api.getMe(),

  // ---------------------------------------------------------------
  // Password reset endpoints (Driver #20).
  //
  // forgotPassword: POSTs the email to /auth/forgot-password. The
  // backend always returns the same anti-enumeration payload whether
  // or not the email has an account, so callers should display a
  // generic "check your email" message regardless of the resolved
  // value (or rejection) here.
  //
  // confirmReset: POSTs the token + new password to the existing
  // /auth/confirm-reset endpoint. Throws ApiError on invalid/expired
  // tokens; callers surface the error to the form.
  // ---------------------------------------------------------------
  forgotPassword: async (email: string): Promise<{ success: boolean; message: string }> => {
    return api.request('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  },

  confirmReset: async (token: string, newPassword: string): Promise<{ message: string }> => {
    return api.request('/auth/confirm-reset', {
      method: 'POST',
      body: JSON.stringify({ token, new_password: newPassword }),
    });
  },

  // ---------------------------------------------------------------
  // TOTP MFA endpoints (Driver #10).
  //
  // These hit the /auth/totp/* routes implemented in
  // backend/api/auth/totp_routes.py. The frontend SecurityTab +
  // TwoFactorSettings components compose these into the enable /
  // disable / verify flows.
  // ---------------------------------------------------------------

  /** Begin TOTP enrollment: returns a fresh secret + otpauth:// URI for QR. */
  totpSetup: async (): Promise<{ secret: string; uri: string }> => {
    return api.request('/auth/totp/setup', { method: 'POST' });
  },

  /** Verify a 6-digit code from the authenticator app, enable MFA,
   *  and receive 10 one-time backup codes (shown to the user once). */
  totpEnable: async (code: string): Promise<{ backup_codes: string[] }> => {
    return api.request('/auth/totp/enable', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  },

  /** Re-authenticate and disable MFA. Either a current TOTP ``code``
   *  or a ``password`` re-entry is required. Clears the secret + all
   *  backup codes. */
  totpDisable: async (params: { code?: string; password?: string }): Promise<{ message: string }> => {
    return api.request('/auth/totp/disable', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  },

  /** Complete MFA login: submit the challenge_token from /login plus
   *  either a TOTP ``code`` or a ``backup_code`` to receive access +
   *  refresh tokens. */
  totpVerify: async (params: {
    challenge_token: string;
    code?: string;
    backup_code?: string;
  }): Promise<{
    access_token: string;
    refresh_token: string;
    token_type: string;
    expires_in: number;
    user: unknown;
  }> => {
    return api.request('/auth/totp/verify', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  },
};

export const agenciesApi = {
  getById: async (agencyId: string) => api.getAgency(agencyId),
  list: async () => api.listAgencies(),
  create: async (data: { name: string; slug: string; subscription_plan?: string; deployment_type?: string }) => {
    return api.request<any>('/agencies', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  update: async (agencyId: string, data: { name?: string; subscription_plan?: string }) => api.updateAgency(agencyId, data),
};


export { api };

// ============================================================================
// Custom Error Classes (BUG-005 GREEN Phase)
// ============================================================================

/**
 * Custom ApiError class that wraps API error responses.
 * Contains error code, message, and optional details.
 */
class ApiErrorClass extends Error {
  code: string;
  /** HTTP status code from the failing response. Available so callers can
   * branch on status (e.g. 409 conflict vs 502 upstream) instead of
   * pattern-matching against the message string. */
  status: number;
  details?: {
    fields?: Array<{ field: string; message: string }>;
    retry_after?: number;
  };

  constructor(error: ApiError, status: number) {
    super(error.message);
    this.name = 'ApiError';
    this.code = error.code;
    this.status = status;
    this.details = error.details;
  }

  /**
   * Check if this is a specific error code.
   */
  isErrorCode(code: string): boolean {
    return this.code === code;
  }

  /**
   * Check if this is a validation error with field details.
   */
  hasValidationErrors(): boolean {
    return this.code === 'VALIDATION_ERROR' &&
      !!this.details?.fields &&
      this.details.fields.length > 0;
  }

  /**
   * Get validation errors as a record.
   */
  getValidationErrors(): Record<string, string> {
    if (!this.hasValidationErrors()) return {};

    return this.details!.fields!.reduce((acc, field) => {
      acc[field.field] = field.message;
      return acc;
    }, {} as Record<string, string>);
  }

  /**
   * Get user-friendly display message.
   */
  getDisplayMessage(): string {
    return getDisplayMessage(this);
  }
}

/**
 * Type guard to check if an error is an ApiErrorClass instance.
 */
export { ApiErrorClass };
export type { ApiError } from './errors';
export function isApiError(error: unknown): error is ApiErrorClass {
  return error instanceof ApiErrorClass;
}
